import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar, UtensilsCrossed, ChevronLeft, ChevronRight, BookOpen, Flag, Star, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const DAYS = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes'];
const DAY_LABELS = { lunes: 'Lunes', martes: 'Martes', miercoles: 'Miércoles', jueves: 'Jueves', viernes: 'Viernes' };

const CATEGORIES = [
  { value: 'reunion', label: 'Reunión', color: 'bg-blue-100 text-blue-700' },
  { value: 'feriado', label: 'Feriado', color: 'bg-red-100 text-red-700' },
  { value: 'actividad', label: 'Actividad', color: 'bg-green-100 text-green-700' },
  { value: 'examen', label: 'Examen', color: 'bg-purple-100 text-purple-700' },
  { value: 'evento', label: 'Evento', color: 'bg-orange-100 text-orange-700' },
];

const getCategoryInfo = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[4];

// Local date helpers (avoid UTC off-by-one in Puerto Rico / UTC-4).
const todayLocalStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Formats a 'YYYY-MM-DD' string for display WITHOUT timezone shift.
const formatLocalDate = (dateStr, options) => {
  if (!dateStr) return '';
  const [y, m, d] = String(dateStr).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  return new Date(y, m - 1, d).toLocaleDateString('es-ES', options);
};

const ParentCalendarsView = () => {
  const { token } = useAuth();
  const api = axios.create({ baseURL: API_URL, headers: { Authorization: `Bearer ${token}` } });

  const [events, setEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [lunchMenus, setLunchMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [evRes, upRes, luRes] = await Promise.all([
        api.get('/api/calendars/events'),
        api.get('/api/calendars/events/upcoming?limit=5'),
        api.get('/api/calendars/lunch'),
      ]);
      setEvents(evRes.data || []);
      setUpcomingEvents(upRes.data || []);
      setLunchMenus(luRes.data || []);
    } catch (e) { console.error('Error loading calendars:', e); }
    finally { setLoading(false); }
  };

  const getMonthDays = () => {
    const [year, month] = currentMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startPad = (firstDay.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < startPad; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentMonth}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.event_date === dateStr);
  };

  const changeMonth = (delta) => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const monthName = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    return new Date(y, m - 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  };

  // Find current week lunch menu
  const getCurrentWeekMenu = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today);
    monday.setDate(diff);
    const mondayStr = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
    return lunchMenus.find(m => m.week_start === mondayStr) || lunchMenus[0];
  };

  if (loading) {
    return <div className="text-center py-12"><div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div></div>;
  }

  const currentLunch = getCurrentWeekMenu();

  return (
    <div className="space-y-6" data-testid="parent-calendars">
      {/* Upcoming Events */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-5 h-5 text-orange-500" />
            Próximos Eventos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingEvents.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-4">No hay eventos próximos</p>
          ) : (
            <div className="space-y-2">
              {upcomingEvents.map(ev => {
                const cat = getCategoryInfo(ev.category);
                return (
                  <div key={ev.id} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <div className={`w-2 h-full min-h-[40px] rounded-full flex-shrink-0 ${cat.color.split(' ')[0]}`}></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-medium text-slate-800">{ev.title}</p>
                        <Badge className={`${cat.color} text-[10px]`}>{cat.label}</Badge>
                      </div>
                      <p className="text-xs text-slate-500">{formatLocalDate(ev.event_date, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                      {ev.description && <p className="text-sm text-slate-600 mt-1">{ev.description}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => changeMonth(-1)}><ChevronLeft className="w-5 h-5" /></Button>
            <h3 className="text-lg font-semibold text-slate-800 capitalize">{monthName()}</h3>
            <Button variant="ghost" size="sm" onClick={() => changeMonth(1)}><ChevronRight className="w-5 h-5" /></Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
            {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => (
              <div key={d} className="bg-slate-50 py-2 text-center text-xs font-semibold text-slate-500 uppercase">{d}</div>
            ))}
            {getMonthDays().map((day, i) => {
              const dayEvents = getEventsForDay(day);
              const isToday = day && `${currentMonth}-${String(day).padStart(2, '0')}` === todayLocalStr();
              return (
                <div key={i} className={`bg-white min-h-[70px] p-1 ${!day ? 'bg-slate-50' : ''}`}>
                  {day && (
                    <>
                      <span className={`text-xs font-medium inline-block w-6 h-6 text-center leading-6 rounded-full ${isToday ? 'bg-orange-500 text-white' : 'text-slate-600'}`}>{day}</span>
                      <div className="mt-0.5 space-y-0.5">
                        {dayEvents.slice(0, 2).map(ev => {
                          const cat = getCategoryInfo(ev.category);
                          return <div key={ev.id} className={`text-[10px] truncate px-1 py-0.5 rounded ${cat.color}`} title={ev.title}>{ev.title}</div>;
                        })}
                        {dayEvents.length > 2 && <div className="text-[10px] text-slate-400">+{dayEvents.length - 2}</div>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-4">
            {CATEGORIES.map(cat => (
              <div key={cat.value} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${cat.color.split(' ')[0]}`}></div>
                <span className="text-xs text-slate-500">{cat.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Lunch Menu */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <UtensilsCrossed className="w-5 h-5 text-orange-500" />
            Menú de Almuerzo
            {currentLunch && <Badge variant="outline" className="ml-2 text-xs">Semana del {new Date(currentLunch.week_start + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!currentLunch ? (
            <p className="text-slate-500 text-sm text-center py-4">No hay menú de almuerzo disponible</p>
          ) : (
            <>
              {/* Desktop/tablet grid */}
              <div className="hidden sm:grid sm:grid-cols-3 md:grid-cols-5 gap-3">
                {DAYS.map(day => {
                  const item = currentLunch.items?.find(i => i.day === day);
                  const todayDay = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'][new Date().getDay()];
                  const isToday = day === todayDay;
                  return (
                    <div key={day} className={`rounded-lg p-3 text-center border transition-all ${isToday ? 'bg-orange-50 border-orange-200 ring-2 ring-orange-100' : 'bg-slate-50 border-slate-100'}`}>
                      <p className={`text-xs font-semibold uppercase mb-2 ${isToday ? 'text-orange-600' : 'text-slate-500'}`}>
                        {DAY_LABELS[day]}
                        {isToday && <span className="block text-[10px] font-normal text-orange-400">Hoy</span>}
                      </p>
                      <p className={`text-sm font-medium break-words ${isToday ? 'text-orange-800' : 'text-slate-800'}`}>{item?.menu || '-'}</p>
                      {item?.notes && <p className="text-xs text-slate-500 mt-1 break-words">{item.notes}</p>}
                    </div>
                  );
                })}
              </div>
              {/* Mobile stacked list */}
              <div className="sm:hidden space-y-2" data-testid="lunch-mobile-list">
                {DAYS.map(day => {
                  const item = currentLunch.items?.find(i => i.day === day);
                  const todayDay = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'][new Date().getDay()];
                  const isToday = day === todayDay;
                  return (
                    <div key={day} className={`rounded-lg p-3 flex items-start gap-3 border transition-all ${isToday ? 'bg-orange-50 border-orange-200 ring-2 ring-orange-100' : 'bg-slate-50 border-slate-100'}`}>
                      <div className="w-16 flex-shrink-0 pt-0.5">
                        <span className={`text-xs font-semibold uppercase ${isToday ? 'text-orange-600' : 'text-slate-500'}`}>{DAY_LABELS[day]}</span>
                        {isToday && <span className="block text-[10px] font-normal text-orange-400">Hoy</span>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium break-words ${isToday ? 'text-orange-800' : 'text-slate-800'}`}>{item?.menu || '-'}</p>
                        {item?.notes && <p className="text-xs text-slate-500 mt-1 break-words">{item.notes}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ParentCalendarsView;
